export interface ThreadScrollPosition {
  readonly anchor?: { readonly messageId: string; readonly viewOffset: number } | null;
  readonly offset: number;
  readonly isAtEnd: boolean;
}

const positions = new Map<string, ThreadScrollPosition>();
const MAX_SAVED_POSITIONS = 100;

export function readThreadScrollPosition(key: string): ThreadScrollPosition | null {
  return positions.get(key) ?? null;
}

export function saveThreadScrollPosition(key: string, position: ThreadScrollPosition): void {
  if (!Number.isFinite(position.offset) || position.offset < 0) return;
  positions.delete(key);
  positions.set(key, position);
  if (positions.size > MAX_SAVED_POSITIONS) {
    const oldest = positions.keys().next().value;
    if (oldest !== undefined) positions.delete(oldest);
  }
}

export function transferThreadScrollPosition(source: string, destination: string): void {
  const position = positions.get(source);
  if (position) saveThreadScrollPosition(destination, position);
}

interface ScrollAnchorRow {
  readonly kind: string;
  readonly id: string;
}

function messageAnchorId(row: ScrollAnchorRow): string {
  return row.id.startsWith("message:") ? row.id.slice("message:".length) : row.id;
}

export function captureThreadScrollAnchor(
  rows: readonly ScrollAnchorRow[],
  visibleStart: number,
  scrollOffset: number,
  positionAtIndex: (index: number) => number,
): { readonly messageId: string; readonly viewOffset: number } | null {
  let index = rows.findIndex(
    (row, candidate) => candidate >= visibleStart && row.kind === "message",
  );
  if (index < 0) index = rows.findLastIndex((row) => row.kind === "message");
  const row = rows[index];
  if (!row) return null;
  const viewOffset = positionAtIndex(index) - scrollOffset;
  return Number.isFinite(viewOffset) ? { messageId: messageAnchorId(row), viewOffset } : null;
}

export function resolveThreadScrollAnchor(
  rows: readonly ScrollAnchorRow[],
  position: ThreadScrollPosition | null,
): { readonly index: number; readonly viewOffset: number } | null {
  if (!position?.anchor || position.isAtEnd) return null;
  const index = rows.findIndex(
    (row) => row.kind === "message" && messageAnchorId(row) === position.anchor?.messageId,
  );
  return index < 0 ? null : { index, viewOffset: position.anchor.viewOffset };
}
